import { useRef, useCallback } from 'react';

export default function useDragScroll() {
  const ref       = useRef(null);
  const dragging  = useRef(false);
  const startX    = useRef(0);
  const scrollLeft= useRef(0);
  const lastX     = useRef(0);
  const lastTime  = useRef(0);
  const velocity  = useRef(0);
  const rafId     = useRef(null);

  const stopMomentum = () => {
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
  };

  // Recursive momentum loop — decays velocity each frame (ease-out glide)
  const tick = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    velocity.current *= 0.95; // friction: lower = snappier, higher = longer glide
    if (Math.abs(velocity.current) < 0.4) return;
    el.scrollLeft -= velocity.current;
    rafId.current = requestAnimationFrame(tick);
  }, []);

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    stopMomentum();
    dragging.current   = true;
    startX.current     = e.pageX - ref.current.offsetLeft;
    scrollLeft.current = ref.current.scrollLeft;
    lastX.current      = e.pageX;
    lastTime.current   = performance.now();
    velocity.current   = 0;
    ref.current.style.cursor     = 'grabbing';
    ref.current.style.userSelect = 'none';
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    e.preventDefault();

    const now = performance.now();
    const dt  = now - lastTime.current;

    if (dt > 0) {
      // Scale dx to a per-frame (≈16 ms) velocity estimate
      velocity.current = ((e.pageX - lastX.current) / dt) * 16;
    }
    lastX.current    = e.pageX;
    lastTime.current = now;

    const walk = (e.pageX - ref.current.offsetLeft) - startX.current;
    ref.current.scrollLeft = scrollLeft.current - walk;
  }, []);

  const stopDrag = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    ref.current.style.cursor     = 'grab';
    ref.current.style.userSelect = '';

    if (Math.abs(velocity.current) > 1) {
      rafId.current = requestAnimationFrame(tick);
    }
  }, [tick]);

  return {
    ref,
    props: {
      ref,
      onMouseDown,
      onMouseMove,
      onMouseUp:    stopDrag,
      onMouseLeave: stopDrag,
      style: { cursor: 'grab' },
    },
  };
}
